import { useEffect, useState } from "react";
import Home from "./pages/Home";
import ArabicHome from "./pages/ArabicHome";
import AfterPayment from "./pages/AfterPayment";
import CriticalCaseDetails from "./pages/CriticalCaseDetails";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import About from "./pages/About";
import Directors from "./pages/Directors";
import OrganizationStructure from "./pages/OrganizationStructure";
import FoundationPolicies from "./pages/FoundationPolicies";
import FoundationPolicyDetails from "./pages/FoundationPolicyDetails";
import CriticalCases from "./pages/CriticalCases";
import OurProjects from "./pages/OurProjects";
import News from "./pages/News";
import NewsDetails from "./pages/NewsDetails";
import Magazines from "./pages/Magazines";
import BanksAccounts from "./pages/BanksAccounts";
import OpenData from "./pages/OpenData";
import ContactUs from "./pages/ContactUs";
import QuickDonation from "./pages/QuickDonation";
import InKindDonation from "./pages/InKindDonation";
import InKindAssistance from "./pages/InKindAssistance";
import SubmitAidRequest from "./pages/SubmitAidRequest";
import Services from "./pages/Services";
import ServiceDetails from "./pages/ServiceDetails";
import AidApplication from "./pages/AidApplication";
import PartnerApplication from "./pages/PartnerApplication";
import BoardMemberApplication from "./pages/BoardMemberApplication";
import TrackAidRequestStatus from "./pages/TrackAidRequestStatus";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import UaePassCallback from "./pages/UaePassCallback";
import BeneficentDashboard from "./pages/BeneficentDashboard";
import AidRequestDetails from "./pages/AidRequestDetails";
import PrayerTimes from "./pages/PrayerTimes";
import HomeOptionTwo from "./pages/HomeOptionTwo";
import HomeOptionThree from "./pages/HomeOptionThree";
import AppointmentVerification from "./pages/AppointmentVerification";
import RahmaChatHistory from "./pages/RahmaChatHistory";
import FamilyHeadVerificationAdmin from "./pages/FamilyHeadVerificationAdmin";
import DocumentVerificationAdmin from "./pages/DocumentVerificationAdmin";
import ComplaintPortal from "./pages/ComplaintPortal";
import { clearStoredAuth, getStoredToken, isAuthenticated, isUaePassConnected, setAuthReturnPath, UAE_PASS_REQUIRED_KEY } from "./auth";
import "./App.css";

const readLocation = () => {
  const currentPath = window.location.pathname.toLowerCase();
  const isDefaultHome = currentPath === "/" || currentPath === "/index.html";
  if (isDefaultHome) {
    const destination = `/ar${window.location.search}${window.location.hash}`;
    window.history.replaceState({}, "", destination);
    return { path: "/ar", search: window.location.search };
  }
  return { path: currentPath, search: window.location.search };
};

function RegistrationRedirect({ isArabic }) {
  useEffect(() => {
    const destination = isArabic ? "/ar/login" : "/login";
    window.history.replaceState({}, "", `${destination}${window.location.search}${window.location.hash}`);
    window.dispatchEvent(new Event("app:navigate"));
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [isArabic]);

  return <Login language={isArabic ? "ar" : "en"} />;
}

function App() {
  const [location, setLocation] = useState(readLocation);
  const { path: rawPath, search } = location;
  const path = rawPath.startsWith("/ar/") ? rawPath.slice(3) || "/" : rawPath;
  const isArabicRoute = rawPath === "/ar" || rawPath.startsWith("/ar/");

  useEffect(() => {
    const updatePath = () => setLocation(readLocation());
    window.addEventListener("popstate", updatePath);
    window.addEventListener("app:navigate", updatePath);
    return () => {
      window.removeEventListener("popstate", updatePath);
      window.removeEventListener("app:navigate", updatePath);
    };
  }, []);

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    const shouldLocalizeArabic = rawPath.startsWith("/ar");
    const localizeArabicResponse = (value) => {
      if (Array.isArray(value)) return value.map(localizeArabicResponse);
      if (!value || typeof value !== "object") return value;

      const localized = Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, localizeArabicResponse(entry)]));
      Object.entries(value).forEach(([key, entry]) => {
        if (!key.endsWith("_ar") || entry === null || entry === undefined || entry === "") return;
        const baseKey = key.slice(0, -3);
        localized[baseKey] = entry;
        localized[`${baseKey}_en`] = entry;
      });
      return localized;
    };

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      if (response.status === 401 && getStoredToken()) {
        const returnPath = `${window.location.pathname}${window.location.search}`;
        const isLoginPage = /^\/(?:ar\/)?login(?:[/?#]|$)/i.test(returnPath);
        if (!isLoginPage) {
          setAuthReturnPath(returnPath);
          clearStoredAuth();
          const loginPath = window.location.pathname.toLowerCase().startsWith("/ar/") ? "/ar/login" : "/login";
          window.history.replaceState({}, "", loginPath);
          window.dispatchEvent(new Event("app:navigate"));
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }
      if (shouldLocalizeArabic) {
        const json = response.json.bind(response);
        response.json = async () => localizeArabicResponse(await json());
      }
      return response;
    };

    return () => { window.fetch = originalFetch; };
  }, [rawPath]);

  useEffect(() => {
    const isAidApplication = path === "/aid-application" || path === "/aid-application.html" || path === "/apply";
    const isProtectedRoute = path === "/beneficent-dashboard" || path === "/beneficent-dashboard.html" || isAidApplication || path === "/partner-application" || path === "/board-member-application" || path === "/track-aid-request-status" || path === "/track-aid-request-status.html" || path === "/aid-request-details" || path === "/aid-request-details.html" || path === "/crm/rahma-chat-history" || path === "/crm/family-head-verification" || path === "/crm/document-verification";
    const authenticated = isAuthenticated();
    const needsAuthentication = isProtectedRoute && !authenticated;
    const needsUaePass = isAidApplication && authenticated && !isUaePassConnected();

    if (needsAuthentication || needsUaePass) {
      setAuthReturnPath(`${window.location.pathname}${window.location.search}`);
      if (isAidApplication) window.sessionStorage.setItem(UAE_PASS_REQUIRED_KEY, "true");
      clearStoredAuth();
      const loginPath = isArabicRoute ? "/ar/login" : "/login";
      window.history.replaceState({}, "", loginPath);
      setLocation({ path: loginPath, search: "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [isArabicRoute, path]);

  const queryParams = new URLSearchParams(search);
  const isUaePassCallback = (queryParams.has("code") && queryParams.has("state")) ||
    queryParams.has("error") ||
    path === "/uae-pass-callback" ||
    path === "/uaepass-callback" ||
    path === "/uaepasslogin" ||
    path === "/uae-pass/login-callback";

  if (isUaePassCallback) {
    return <UaePassCallback />;
  }

  if (path === "/" || path === "/index.html") {
    return <Home />;
  }

  if (path === "/home" || path === "/home.html") {
    return isArabicRoute ? <ArabicHome /> : <Home />;
  }

  if (rawPath === "/ar" || rawPath === "/ar/" || rawPath === "/ar/index.html") {
    return <ArabicHome />;
  }

  if (path === "/home-option-2" || path === "/home-option-2.html") {
    return <HomeOptionTwo language={isArabicRoute ? "ar" : "en"} />;
  }
if (path === "/home-option-3" || path === "/home-option-3.html") {
    return <HomeOptionThree language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/login" || path === "/login.html") {
    return <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/forgot-password" || path === "/forgot-password.html") {
    return <ForgotPassword language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/registration" || path === "/registration.html") {
    return <RegistrationRedirect isArabic={isArabicRoute} />;
  }
  if (path === "/appointment-verification" || path === "/appointment-verification.html") {
    return <AppointmentVerification language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/crm/rahma-chat-history") {
    return isAuthenticated() ? <RahmaChatHistory /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/crm/family-head-verification") {
    return isAuthenticated() ? <FamilyHeadVerificationAdmin /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/crm/document-verification") {
    return isAuthenticated() ? <DocumentVerificationAdmin /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/complaint") {
    return <ComplaintPortal language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/beneficent-dashboard" || path === "/beneficent-dashboard.html") {
    return isAuthenticated() ? <BeneficentDashboard language={isArabicRoute ? "ar" : "en"} /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/aid-request-details" || path === "/aid-request-details.html") {
    return isAuthenticated() ? <AidRequestDetails language={isArabicRoute ? "ar" : "en"} /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/after-payment" || path === "/after-payment.html") {
    return <AfterPayment />;
  }

  if (path === "/prayer-times" || path === "/prayer-times.html") {
    return <PrayerTimes />;
  }

  if (path === "/critical-cases-details" || path === "/critical-cases-details-page.html") {
    return <CriticalCaseDetails type="Case" language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/project-details" || path === "/donation-details-page.html") {
    return <CriticalCaseDetails type="Project" language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/news-details" || path === "/news-details.html") {
    return <NewsDetails language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/news" || path === "/tarahumnews.html") {
    return <News language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/magazines" || path === "/magazines.html") {
    return <Magazines language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/banks-accounts" || path === "/banks-accounts.html") {
    return <BanksAccounts language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/open-data" || path === "/open-data.html" || path === "/open_data.html" || path === "/ar/open_data.html") {
    return <OpenData language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/contact-us" || path === "/contact-us.html" || path === "/ar/contact-us.html") {
    return <ContactUs language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/critical-cases" || path === "/cases_donations.html") {
    return <CriticalCases language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/our-projects" || path === "/our-projects.html") {
    return <OurProjects language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/quick-donation" || path === "/quick-donation.html") {
    return <QuickDonation language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/in-kind-donation" || path === "/in_kind_donation.html") {
    return <InKindDonation language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/in-kind-assistance") {
    return isAuthenticated() ? <InKindAssistance language={isArabicRoute ? "ar" : "en"} /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/submit-aid-request" || path === "/submit-aid-request.html") {
    return <SubmitAidRequest language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/services" || path === "/services.html") {
    return <Services language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/service-details" || path === "/service-details.html") {
    return <ServiceDetails language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/aid-application" || path === "/aid-application.html") {
    return isAuthenticated() ? <AidApplication language={isArabicRoute ? "ar" : "en"} /> : <Login />;
  }
  if (path === "/partner-application") {
    return isAuthenticated() ? <PartnerApplication /> : <Login />;
  }
  if (path === "/board-member-application") {
    return isAuthenticated() ? <BoardMemberApplication language={isArabicRoute ? "ar" : "en"} /> : <Login language={isArabicRoute ? "ar" : "en"} />;
  }
  if (path === "/apply") {
    return <PartnerApplication language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/track-aid-request-status" || path === "/track-aid-request-status.html") {
    return isAuthenticated() ? <TrackAidRequestStatus language={isArabicRoute ? "ar" : "en"} /> : <Login />;
  }

  if (path === "/cart" || path === "/cart.html") {
    return <Cart language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/checkout" || path === "/checkout.html" || path === "/check-out" || path === "/check-out.html") {
    return <Checkout language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/about-us" || path === "/about-us.html") {
    return <About language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/directors" || path === "/directors.html") {
    return <Directors language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/organization-structure" || path === "/structure.html") {
    return <OrganizationStructure language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path === "/foundation-policies" || path === "/policies.html") {
    return <FoundationPolicies language={isArabicRoute ? "ar" : "en"} />;
  }

  if (path.startsWith("/foundation-policies/") || path === "/intellectual_property_rights_protection_policy.html") {
    return <FoundationPolicyDetails language={isArabicRoute ? "ar" : "en"} />;
  }

  return <Home />;
}

export default App;
